import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { instanceToInstance, plainToInstance } from "class-transformer";
import { BehaviorSubject, EMPTY, Observable, Observer, Subject, Subscription, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { JSend } from '../../models/jsend.model';
import { CacheService } from '../../services/cache.service';
import { CurrentDateService } from '../../services/current-date.service';
import { PusherService } from '../../services/pusher.service';
import { TenantService } from '../../services/tenant.service';
import { FlashService } from '../../ui/services/flash.service';
import { Account } from '../models/account.model';
import { Login } from '../models/login.model';
import { Token } from '../models/token.model';
import { NotificationChannel, User } from '../models/user.model';

@Injectable()
export class LoginService {

  private loginUrl = '/:apiBase/login';
  private logoutUrl = '/:apiBase/logout';
  private usersUrl = '/:apiBase/users';
  private userByIdUrl = this.usersUrl + '/:userId';
  private userChannels = this.usersUrl + '/channels';
  private sessionUrl = '/:apiBase/verification-status';
  private accountsUrl = this.userByIdUrl + '/accounts';
  private requestPassword = this.usersUrl + '/passwords-request';
  private passwordVerificationUrl = this.usersUrl + '/password-verification/:token';
  private tokenVerificationUrl = '/:apiBase/validate-invitation-token/:token';
  private emailVerificationUrl = this.userByIdUrl + '/verification/:token';
  private changePasswordUrl = this.userByIdUrl + '/password/:token';
  // TODO: The path of this resource do not follow the conventions
  private updatePasswordUrl = this.userByIdUrl + '/update-password';
  private reverificationEmail = '/:apiBase/users/email/resend-verification';
  private verificationRemainingToRetry = '/:apiBase/users/email/verification-retry-time';
  private userPhoneUrl = this.userByIdUrl + '/phone';
  private verificatePhoneCodeUrl = this.userPhoneUrl + '/verification';
  private googleApiGetUserInfo = ` https://www.googleapis.com/oauth2/v2/userinfo?access_token=:accessToken`;

  private redirectUrl: string;
  private user = new BehaviorSubject<User>(null);
  private token = new BehaviorSubject<Token>(null);
  private logoutSubs: Subscription;
  private _collectionSubjects: { [eventKey: string]: BehaviorSubject<Account[]> } = {};
  private readonly CACHE_MINUTES = 60 * 24 * 365;

  constructor(
    private router: Router,
    private http: HttpClient,
    private pusherService: PusherService,
    private flashService: FlashService,
    private translateService: TranslateService,
    private currentDate: CurrentDateService,
    private cacheService: CacheService,
    private tenantService: TenantService
  ) {
    const login: Login = this.cacheService.get('login');

    if (login) {
      this.user.next(login.user);
      this.token.next(login.token_data);
    }

    this.logoutSubs = this.user.pipe(
      switchMap(user => user ?
        this.pusherService.listen('private_' + user.id, 'logout', 0) :
        EMPTY)
    ).subscribe(event => {
      this.logout(false, true);
    });
  }

  /**
   * Parses the login data, stores it in local storage, and updates the relevant observables.
   * 
   * @param {Login} login - The login data to parse.
   * @returns {Login} - The parsed login data.
   */
  private parseLogin(login: Login): Login {
    // Store login data in cache
    this.cacheService.set('login', {
      user: login.user,
      token_data: login.token_data || this.token.value
    }, this.CACHE_MINUTES);

    // Convert plain login object to class instance
    const loginInstance = plainToInstance(Login, login);

    // Update user and token observables
    if (loginInstance.user) this.user.next(loginInstance.user);
    if (loginInstance.token_data) this.token.next(loginInstance.token_data);

    // Set language preference if available
    if (loginInstance.user && loginInstance.user.language) {
      this.translateService.use(loginInstance.user.language);
    }

    return loginInstance;
  }

  public login(data: any): Observable<Login> {
    const stream = this.http.post<Login>(this.loginUrl, { ...data, tenant_id: this.tenantService.tenant.id });

    return stream.pipe(map(login => {
      return this.parseLogin(login);
    }));
  }

  // ─── Auth0 PKCE flow ──────────────────────────────────────────────────────

  /**
   * Opens an Auth0 popup (PKCE) and resolves when the user has authenticated.
   * The parent window retains the PKCE verifier in sessionStorage; the popup
   * only relays the authorization code back via postMessage.
   *
   * @param screenHint 'login' (default) | 'signup' — maps to Auth0 screen_hint param.
   */
  public loginWithAuth0Popup(screenHint: 'login' | 'signup' = 'login'): Promise<void> {
    const auth0Config = environment.auth0;
    if (!auth0Config) return Promise.reject(new Error('Auth0 config missing in environment'));

    return new Promise(async (resolve, reject) => {
      const verifier  = this.generateCodeVerifier();
      const challenge = await this.generateCodeChallenge(verifier);
      const state     = this.generateState();
      const tenant    = this.tenantService.tenant;

      sessionStorage.setItem('pkce_verifier', verifier);
      sessionStorage.setItem('oauth_state', state);

      const params = new URLSearchParams({
        response_type:         'code',
        client_id:             auth0Config.clientId,
        redirect_uri:          `${window.location.origin}/auth/callback`,
        scope:                 'openid profile email offline_access',
        audience:              auth0Config.audience,
        state,
        code_challenge:        challenge,
        code_challenge_method: 'S256',
        ...(tenant?.id && { tenant_id: tenant.id }),
        ...(screenHint === 'signup' && { screen_hint: 'signup' }),
        ...(screenHint === 'signup' && { verify_callback_url: `${window.location.origin}/verify-callback` }),
      });

      const url   = `https://${auth0Config.domain}/authorize?${params}`;
      const popup = window.open(url, 'auth0_login', this.popupFeatures());

      if (!popup) {
        // Popup blocked — fall back to full-page redirect
        window.location.href = url;
        return;
      }

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin || event.source !== popup) return;
        const data = event.data;
        if (!data || data.type !== 'AUTH0_CODE') return;

        cleanup();

        if (data.error || !data.code) {
          sessionStorage.removeItem('pkce_verifier');
          sessionStorage.removeItem('oauth_state');
          reject(new Error(data.error || 'auth_cancelled'));
          return;
        }

        this.handleAuth0Callback(data.code, data.state).subscribe({
          next:  ()    => resolve(),
          error: (err) => reject(err),
        });
      };

      // Watchdog: user closed the popup without completing auth
      const interval = setInterval(() => {
        if (popup.closed) {
          cleanup();
          sessionStorage.removeItem('pkce_verifier');
          sessionStorage.removeItem('oauth_state');
          reject(new Error('popup_closed'));
        }
      }, 600);

      const cleanup = () => {
        clearInterval(interval);
        window.removeEventListener('message', onMessage);
      };

      window.addEventListener('message', onMessage);
    });
  }

  private popupFeatures(): string {
    const w    = 480, h = 660;
    const left = Math.round(window.screenX + (window.outerWidth  - w) / 2);
    const top  = Math.round(window.screenY + (window.outerHeight - h) / 2);
    return `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,resizable=yes`;
  }

  /**
   * Attempts SSO silently via a hidden iframe (prompt=none), relying on the
   * Auth0 tenant session cookie set by a previous login on agree OR agrology.
   * Resolves true+logs the user in if a session was found; resolves false
   * (never rejects) if the user must see the Lock screen — e.g. no session,
   * login_required, consent_required, or timeout. Callers should always fall
   * back to the normal loginWithAuth0Popup() flow on a false result.
   */
  public trySilentAuth(): Promise<boolean> {
    const auth0Config = environment.auth0;
    if (!auth0Config) return Promise.resolve(false);

    return new Promise(async (resolve) => {
      const verifier  = this.generateCodeVerifier();
      const challenge = await this.generateCodeChallenge(verifier);
      const state     = this.generateState();
      const tenant    = this.tenantService.tenant;

      sessionStorage.setItem('pkce_verifier', verifier);
      sessionStorage.setItem('oauth_state', state);

      const params = new URLSearchParams({
        response_type:         'code',
        client_id:             auth0Config.clientId,
        redirect_uri:          `${window.location.origin}/auth/callback`,
        scope:                 'openid profile email offline_access',
        audience:              auth0Config.audience,
        state,
        code_challenge:        challenge,
        code_challenge_method: 'S256',
        prompt:                'none',
        ...(tenant?.id && { tenant_id: tenant.id }),
      });

      const url = `https://${auth0Config.domain}/authorize?${params}`;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');

      let settled = false;
      const finish = (result: boolean) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(result);
      };

      const timeoutId = setTimeout(() => finish(false), 8000);

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin || event.source !== iframe.contentWindow) return;
        const data = event.data;
        if (!data || data.type !== 'AUTH0_CODE') return;

        if (data.error || !data.code || data.state !== state) {
          // login_required / consent_required / interaction_required / mismatched state
          // all mean "no active session" — the caller falls back to the visible Lock.
          sessionStorage.removeItem('pkce_verifier');
          sessionStorage.removeItem('oauth_state');
          finish(false);
          return;
        }

        this.handleAuth0Callback(data.code, data.state).subscribe({
          next:  () => finish(true),
          error: () => finish(false),
        });
      };

      const cleanup = () => {
        clearTimeout(timeoutId);
        window.removeEventListener('message', onMessage);
        iframe.remove();
      };

      window.addEventListener('message', onMessage);
      iframe.src = url;
      document.body.appendChild(iframe);
    });
  }

  /**
   * Handles the Auth0 callback after redirect. Exchanges the code for tokens,
   * then calls parseLogin() — same contract as the Passport login flow.
   */
  public handleAuth0Callback(code: string, state: string): Observable<Login> {
    const savedState = sessionStorage.getItem('oauth_state');
    const verifier   = sessionStorage.getItem('pkce_verifier');

    if (!verifier || !savedState) {
      return throwError(() => new Error('PKCE session expired — please try again'));
    }

    if (state !== savedState) {
      return throwError(() => new Error('Invalid OAuth state'));
    }

    sessionStorage.removeItem('pkce_verifier');
    sessionStorage.removeItem('oauth_state');

    const activationToken = sessionStorage.getItem('activation_token');
    if (activationToken) sessionStorage.removeItem('activation_token');

    return this.http.post<Login>('/:apiBase/auth/callback', {
      code,
      redirect_uri:    `${window.location.origin}/auth/callback`,
      code_verifier:   verifier,
      ...(activationToken && { activation_token: activationToken }),
    }).pipe(
      map(login => this.parseLogin(login)),
      catchError(err => throwError(() => err))
    );
  }

  /**
   * After the user verifies their email in Auth0, re-syncs the fresh
   * email_verified flag into the current session WITHOUT requiring a re-login.
   * Reuses parseLogin() to update the cached user (same as the legacy verify()).
   */
  public refreshVerification(): Observable<Login> {
    return this.http.post<Login>('/:apiBase/auth/refresh-verification', {}).pipe(
      map(login => this.parseLogin(login)),
      catchError(err => throwError(() => err))
    );
  }

  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const data   = new TextEncoder().encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  private generateState(): string {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Triggers an Auth0 password reset for the given email. Auth0 changes the
   * password and (via the agree-db Change Password script) makes api2 send the
   * agree-branded reset email. Returns an Observable that completes on success.
   */
  public requestAuth0PasswordReset(email: string): Observable<any> {
    const auth0Config = environment.auth0;
    if (!auth0Config) {
      return throwError(() => new Error('Auth0 config missing in environment'));
    }
    return this.http.post(`https://${auth0Config.domain}/dbconnections/change_password`, {
      client_id:  auth0Config.clientId,
      email,
      connection: 'agree-db',
    });
  }

  // ─────────────────────────────────────────────────────────────────────────

  public logout(from_link: boolean = false, reload: boolean = false): void {
    this.token.pipe(
      take(1),
    ).subscribe(token => {

      if (token) {
        this.user.next(null);
        this.token.next(null);
        this._session = undefined;
        this._collectionSubjects = {};
        this.cacheService.clear();
        this.flashService.dismissAll();

        // Redirect to Auth0 logout to clear the SSO session cookie at auth.agree.ag.
        // Both popup and redirect flows establish a session there, so always clear it.
        if (environment.auth0) {
          const params = new URLSearchParams({
            client_id: environment.auth0.clientId,
            returnTo:  window.location.origin,
          });
          window.location.href = `https://${environment.auth0.domain}/v2/logout?${params}`;
          return;
        }
      }

      this.flashService.dismissAll();

      if (reload) {
        window.location.href = '/login';
      } else if (!from_link) {
        this.router.navigate(['/login']);
      }
    });
  }

  public customLogout(backToUrl: string): void {
    this.setRedirectUrl(backToUrl);

    this.token.pipe(
      take(1),
    ).subscribe(token => {
      if (token) {
        this.user.next(null);
        this.token.next(null);
        this._session = undefined;
        this._collectionSubjects = {};
        this.cacheService.clear();
      }

      this.flashService.dismissAll();
      this.router.navigate(['/login']);
    });
  }

  private _session: any;
  public checkMailVerification(): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      if (this._session) {
        observer.next(this._session);
        observer.complete();
      } else {
        const stream = this.http.get<any>(this.sessionUrl);
        const actualLogin = this.cacheService.get('login');

        const done = (response) => {
          this._session = response;
          this.parseLogin(response);

          observer.next(this._session);
          observer.complete();
        };

        if (actualLogin.user?.mail_verified == 1) {
          done(actualLogin);

          return;
        }

        stream.subscribe(response => {
          done(response);
        });
      }
    });
  }

  public validateActivationToken(token: string): Observable<any> {
    const url = this.tokenVerificationUrl.replace(":token", token);
    return this.http.get<any>(url);
  }

  public updatePhone(user: User): Observable<Login> {
    const url = this.userPhoneUrl.replace(":userId", user.id.toString());
    return this.http.put<Login>(url, user);
  }

  public verificatePhoneCode(user: User): Observable<any> {
    const url = this.verificatePhoneCodeUrl.replace(":userId", user.id.toString());
    return this.http.put<any>(url, user);
  }

  public register(data: any, captchaResponse: string): Observable<Login> {
    const observable = new Observable<Login>(subscriber => {
      // TODO: post /users should return a User, not a Login, use headers instead
      const stream = this.http.post<Login>(this.usersUrl, {
        ...data,
        captchaResponse,
        tenant_id: this.tenantService.tenant.id
      });

      stream.subscribe({
        next: login => {
          subscriber.next(this.parseLogin(login));
        },
        error: error => {
          subscriber.error(error);
        }
      });
    });

    return observable;
  }

  public getCurrentUser(): BehaviorSubject<User> {
    return this.user;
  }

  public getCurrentToken(): BehaviorSubject<Token> {
    return this.token;
  }

  private getAccounts(): Observable<Account[]> {
    const url = this.accountsUrl.replace(":userId", (this.user.getValue().id).toString());
    const stream = this.http.get<Account[]>(url, { observe: 'response' });
    return stream.pipe(map(response => {
      // La expresión regular es un workaround para salvar los casos donde el header Date viene con doble fecha
      // Ejemplo: Thu, 21 May 2020 16:20:57 GMT, Thu, 21 May 2020 16:20:57 GMT
      // Funciona tanto para los casos con doble fecha como para los de fecha simple
      this.currentDate.set(new Date(response.headers.get('date').match(/[^,]+,[^,]+/g)[0]));

      return plainToInstance(Account, response.body);
    }));
  }

  public getAccountsForCompany(companyId: Number): Observable<Account[]> {
    const url = this.accountsUrl
      .replace(":userId", (this.user.getValue().id).toString())
      + `?companyId=${companyId}`;

    const stream = this.http.get<Account[]>(url, { observe: 'response' });
    return stream.pipe(map(response => {
      // La expresión regular es un workaround para salvar los casos donde el header Date viene con doble fecha
      // Ejemplo: Thu, 21 May 2020 16:20:57 GMT, Thu, 21 May 2020 16:20:57 GMT
      // Funciona tanto para los casos con doble fecha como para los de fecha simple
      this.currentDate.set(new Date(response.headers.get('date').match(/[^,]+,[^,]+/g)[0]));

      return plainToInstance(Account, response.body);
    }));
  }

  public watchAccounts(): Observable<Account[]> {
    const eventKey = this.user.getValue().id;

    return this.pusherService.subjectManager(
      {
        collection: this._collectionSubjects,
        key: eventKey,
        getData: () => this.getAccounts()
      },
      {
        channel: `private_${eventKey}`,
        event: 'account'
      }
    );
  }

  public setRedirectUrl(url: string): void {
    if (!this.redirectUrl) {
      this.redirectUrl = url;
    }
  }

  public clearRedirectUrl(): void {
    this.redirectUrl = undefined;
  }

  public getRedirectUrl(): string {
    return this.redirectUrl;
  }

  public checkMailVerified(): void {
    this.getCurrentUser().subscribe(user => {
      if (user && !user.mail_verified) {
        this.flashService.report('verification_pending');
      }
    });
  }

  /**
   * Verifies the user's email using the provided userId and token.
   * 
   * @param {number} userId - The ID of the user.
   * @param {string} token - The verification token.
   * @returns {Observable<Login>} - An observable containing the login information.
   */
  public verify(userId: number, token: string): Observable<Login> {
    const loginSubject = new Subject<Login>();
    const url = this.emailVerificationUrl
      .replace(":userId", userId.toString())
      .replace(":token", token);

    const stream = this.http.get<User>(url);

    stream.subscribe({
      next: user => {
        this.parseLogin({ user: user });

        const actualLogin = this.cacheService.get('login');

        loginSubject.next(actualLogin);
        loginSubject.complete();
      },
      error: error => {
        loginSubject.error(error);
        loginSubject.complete();
      }
    });

    return loginSubject;
  }

  public getPasswordVerification(token: string): Observable<any> {
    const url = this.passwordVerificationUrl.replace(":token", token.toString());
    return this.http.get<any>(url);
  }

  public getRequestPassword(data: { email: string }): Observable<any> {
    const stream = this.http.post<any[]>(this.requestPassword, { ...data, tenant_id: this.tenantService.tenant.id });

    return stream;
  }

  public changePassword(data: {
    id: string;
    token: string;
    password: string;
    password_confirmation: string;
  }): Observable<any> {
    const url = this.changePasswordUrl.replace(":userId", data.id.toString()).replace(":token", data.token.toString());

    return this.http.put<any[]>(url, data);
  }

  // TODO: Data model do not follow the conventions
  public updatePassword(data: {
    username?: string;
    password?: string;
    newPassword?: string;
    id?: number;
  }): Observable<any> {
    const url = this.updatePasswordUrl.replace(":userId", data.id.toString());

    return this.http.put<any[]>(url, data);
  }

  public resendVerification(email: string): Observable<JSend<any>> {
    return this.http.post<JSend<any>>(this.reverificationEmail, {
      email,
      tenant_id: this.tenantService.tenant.id
    });
  }

  public getVerificationRemainingTimeToTry(email: string): Observable<JSend<any>> {
    return this.http.post<JSend<any>>(this.verificationRemainingToRetry, {
      email
      // tenant_id: this.tenantService.tenant.id
    });
  }

  public updateUser(user: User): Observable<User> {
    const tempUser = instanceToInstance(user);
    delete tempUser.accounts; // Remove unnecessary information

    const url = this.userByIdUrl.replace(":userId", user.id.toString());

    return this.http.put<User>(url, tempUser).pipe(map(response => plainToInstance(User, response)));
  }

  public delete(user: User): Observable<User> {
    const tempUser = instanceToInstance(user);
    delete tempUser.accounts; // Remove unnecessary information

    const url = this.userByIdUrl.replace(":userId", user.id.toString());

    return this.http.delete<User>(url, {
      body: {
        user: tempUser
      }
    }).pipe(map(response => plainToInstance(User, response)));
  }

  public changeLocalStorageUserLanguage(language: string): void {
    const actualLogin: Login = this.cacheService.get('login');

    if (actualLogin?.user && language) {
      actualLogin.user.language = language;

      this.cacheService.set('login', actualLogin, this.CACHE_MINUTES);
    }
  }

  public getChannels(): Observable<NotificationChannel[]> {
    return this.http.get<NotificationChannel[]>(this.userChannels).pipe(map(response => plainToInstance(NotificationChannel, response)));
  }

  public updateChannels(channels: NotificationChannel[]): Observable<NotificationChannel[]> {
    return this.http.put<NotificationChannel[]>(this.userChannels, channels).pipe(map(response => plainToInstance(NotificationChannel, response)));
  }

  public getGoogleAccountUserInfo(googleAccessToken: string): Observable<any> {
    const url = this.googleApiGetUserInfo.replace(":accessToken", googleAccessToken.toString());
    return this.http.get<any>(url);
  }

  public authenticateWithRedirectedToken(data: { token: string, user: User }): void {
    this.user.next(data.user);

    this.token.next({
      access_token: data.token,
      expires: null,
      refresh_token: null,
      token_type: null
    });

    this.cacheService.set('login', {
      user: this.user.value,
      token_data: this.token.value
    }, this.CACHE_MINUTES);
  }
}
